/**
 * Interface for tracking loading state in `RequestState`.
 */
export interface LoadingState {
  readonly state: 'loading';
  readonly success: false;
  readonly data: null;
  readonly loading: true;
  readonly error: null;
}

/**
 * Interface for tracking failure state in `RequestState`.
 */
export interface FailureState<Failure> {
  readonly state: 'failure';
  readonly success: false;
  readonly data: null;
  readonly loading: false;
  readonly error: Failure;
}

/**
 * Interface for tracking success state in `RequestState`.
 */
export interface SuccessState<Data> {
  readonly state: 'success';
  readonly success: true;
  readonly data: Data;
  readonly loading: false;
  readonly error: null;
}

/**
 * Interface for tracking idle state in `RequestState`.
 */
export interface IdleState {
  readonly state: 'idle';
  readonly success: false;
  readonly data: null;
  readonly loading: false;
  readonly error: null;
}

/**
 * Describes all possible states for `createLoadable` function.
 */
export type RequestState<Data, Failure = unknown> =
  | IdleState
  | LoadingState
  | SuccessState<Data>
  | FailureState<Failure>;

/**
 * Utility function for quick `IdleState` creation.
 */
export function createIdleState(): IdleState {
  return {
    state: 'idle',
    data: null,
    loading: false,
    error: null,
    success: false,
  };
}

/**
 * Utility function for quick `LoadingState` creation
 */
export function createLoadingState(): LoadingState {
  return {
    state: 'loading',
    data: null,
    loading: true,
    error: null,
    success: false,
  };
}

/**
 * Utility function for quick `FailureState` creation
 */
export function createFailureState<Failure = Error>(
  error: Failure
): FailureState<Failure> {
  return {
    state: 'failure',
    data: null,
    loading: false,
    error,
    success: false,
  };
}

/**
 * Utility function for quick `SuccessState` creation.
 */
export function createSuccessState<Data>(data: Data): SuccessState<Data> {
  return { state: 'success', data, loading: false, error: null, success: true };
}
